'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  Button,
  Checkbox,
  Collapsible,
  IconButton,
  Tooltip,
} from '@terra/ui-design-system';
import {
  ArrowDown01Icon,
  ArrowRight01Icon,
  CheckListIcon,
  Undo02Icon,
  SquareIcon,
} from '@terra/ui-icons';
import { Box, HStack, Stack, styled } from '@terra/ui-styled-system';
import {
  formatAdRelationshipDisplayLabel,
  type AdEdgeCategory,
} from '@terra/shared-contracts-internal-network';
import {
  getSubcategorySelectionState,
  type AdEdgeSubcategorySelectionState,
} from './useAdEdgeFilterSelection';

type AdPathFiltersPanelProps = {
  taxonomy: readonly AdEdgeCategory[];
  selected: ReadonlySet<string>;
  isDefaultSelection: boolean;
  onToggleEdgeType: (edgeType: string) => void;
  onSetSubcategorySelected: (input: {
    edgeTypes: readonly string[];
    isSelected: boolean;
  }) => void;
  onSelectAll: () => void;
  onClearAll: () => void;
  onResetToDefault: () => void;
  disabled?: boolean;
};
const CHECKBOX_STATE: Record<
  AdEdgeSubcategorySelectionState,
  boolean | 'indeterminate'
> = {
  all: true,
  none: false,
  partial: 'indeterminate',
};

export function AdPathFiltersPanel({
  taxonomy,
  selected,
  isDefaultSelection,
  onToggleEdgeType,
  onSetSubcategorySelected,
  onSelectAll,
  onClearAll,
  onResetToDefault,
  disabled = false,
}: AdPathFiltersPanelProps) {
  const t = useTranslations('internalNetwork.graph.pathFilters');
  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <Stack
      gap={2}
      bg="canvas"
      borderRadius="sm"
      boxShadow="sm"
      p={3}
      w="272px"
      maxH="full"
      overflowY="auto"
      opacity={disabled ? 0.55 : 1}
    >
      <HStack justifyContent="space-between" alignItems="center">
        <HStack gap={1} alignItems="center">
          <IconButton
            size="xs"
            variant="ghost"
            aria-label={t('title')}
            onClick={() => setIsExpanded((current) => !current)}
          >
            {isExpanded ? (
              <ArrowDown01Icon size={16} />
            ) : (
              <ArrowRight01Icon size={16} />
            )}
          </IconButton>
          <styled.span fontSize="sm" fontWeight="semibold">
            {t('title')}
          </styled.span>
        </HStack>
        <HStack gap={1}>
          <Tooltip title={t('selectAll')}>
            <IconButton
              size="xs"
              variant="ghost"
              aria-label={t('selectAll')}
              disabled={disabled}
              onClick={onSelectAll}
            >
              <CheckListIcon size={16} />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('clearAll')}>
            <IconButton
              size="xs"
              variant="ghost"
              aria-label={t('clearAll')}
              disabled={disabled}
              onClick={onClearAll}
            >
              <SquareIcon size={16} />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('reset')}>
            <IconButton
              size="xs"
              variant="ghost"
              aria-label={t('reset')}
              disabled={disabled || isDefaultSelection}
              onClick={onResetToDefault}
            >
              <Undo02Icon size={16} />
            </IconButton>
          </Tooltip>
        </HStack>
      </HStack>

      {isExpanded &&
        taxonomy.map((category) => (
          <Stack key={category.categoryName} gap={1}>
            {category.subcategories.map((subcategory) => {
              const state = getSubcategorySelectionState({
                edgeTypes: subcategory.edgeTypes,
                selected,
              });

              return (
                <Collapsible.Root key={subcategory.name}>
                  <HStack gap={2} alignItems="center">
                    <Checkbox
                      size="sm"
                      colorPalette="gray"
                      disabled={disabled}
                      checked={CHECKBOX_STATE[state]}
                      onCheckedChange={() =>
                        onSetSubcategorySelected({
                          edgeTypes: subcategory.edgeTypes,
                          isSelected: state !== 'all',
                        })
                      }
                    >
                      <styled.span fontSize="xs">
                        {subcategory.name}
                      </styled.span>
                    </Checkbox>
                    <Collapsible.Trigger asChild>
                      <Button size="xs" variant="ghost">
                        {subcategory.edgeTypes.length}
                      </Button>
                    </Collapsible.Trigger>
                  </HStack>
                  <Collapsible.Content>
                    <Box pl={5} pt={1}>
                      <Stack gap={1}>
                        {subcategory.edgeTypes.map((edgeType) => (
                          <Checkbox
                            key={edgeType}
                            size="sm"
                            colorPalette="gray"
                            disabled={disabled}
                            checked={selected.has(edgeType)}
                            onCheckedChange={() => onToggleEdgeType(edgeType)}
                          >
                            <styled.span fontSize="xs" color="fg.muted">
                              {formatAdRelationshipDisplayLabel(edgeType)}
                            </styled.span>
                          </Checkbox>
                        ))}
                      </Stack>
                    </Box>
                  </Collapsible.Content>
                </Collapsible.Root>
              );
            })}
          </Stack>
        ))}
    </Stack>
  );
}
